import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Card, Col, Form, Nav, Row } from 'react-bootstrap';
import SimpleMDE from 'react-simplemde-editor';
import { toast } from 'react-toastify';
import {
  getTemplateDetailAPI,
  publishTemplateAPI,
  saveTemplateDraftAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import { platformUrl } from '../../../utils/mhNav';
import { applyTemplateSampleVars, templateVariableList } from '../../../mockData/platform/templates';
import styles from './TemplateEditor.module.scss';

export default function TemplateEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    name: '',
    channel: 'teams',
    useCase: 'Reminder',
    subject: '',
    bodyMarkdown: '# Meeting update\n\nHello {{first_name}},\n\n**{{meeting_title}}** at {{start_time}}.\n\n{{meeting_link}}',
  });
  const [previewTab, setPreviewTab] = useState('teams');
  const [localId, setLocalId] = useState(id || null);

  const mdeOptions = useMemo(() => ({ spellChecker: false, minHeight: '260px' }), []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getTemplateDetailAPI(id);
      setDetail(res.data);
      setForm({
        name: res.data.name || '',
        channel: res.data.channel || 'teams',
        useCase: res.data.useCase || 'Reminder',
        subject: res.data.subject || '',
        bodyMarkdown: res.data.bodyMarkdown || '',
      });
      setLocalId(id);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLocalId(id || null);
  }, [id]);

  useEffect(() => {
    if (isNew) {
      setDetail({ validation: { requiredOk: true, variablesMapped: true, arabicOk: false } });
      setLoading(false);
    } else {
      load();
    }
  }, [isNew, load]);

  const previewRendered = useMemo(() => applyTemplateSampleVars(form.bodyMarkdown), [form.bodyMarkdown]);
  const previewSubject = useMemo(() => applyTemplateSampleVars(form.subject), [form.subject]);

  const canPublish =
    detail?.validation?.requiredOk &&
    detail?.validation?.variablesMapped &&
    detail?.validation?.arabicOk;

  const insertVar = (v) => {
    setForm((f) => ({ ...f, bodyMarkdown: `${f.bodyMarkdown} ${v}` }));
  };

  const saveDraft = async () => {
    try {
      const res = await saveTemplateDraftAPI(isNew ? 'new' : localId, form);
      toast.success('Draft saved');
      const newId = res.data.id;
      if (isNew && newId) {
        navigate(platformUrl(`templates/${newId}`), { replace: true });
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const publish = async () => {
    try {
      let tid = localId || id;
      if (isNew || !tid) {
        const res = await saveTemplateDraftAPI('new', form);
        tid = res.data.id;
        navigate(platformUrl(`templates/${tid}`), { replace: true });
      }
      await publishTemplateAPI(tid);
      toast.success('Published');
      if (!isNew) load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading && !isNew) {
    return (
      <div>
        <PageHeader title="Template" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Templates', path: platformUrl('templates') }, { label: 'Edit' }]} />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error && !isNew) {
    return (
      <div>
        <PageHeader title="Template" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Templates', path: platformUrl('templates') }, { label: id }]} />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isNew ? 'New template' : `Edit ${form.name || id}`}
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Templates', path: platformUrl('templates') },
          { label: isNew ? 'New' : 'Edit' },
        ]}
        secondaryActions={
          !isNew && id
            ? [{ label: 'Version history', variant: 'outline-secondary', onClick: () => navigate(platformUrl(`templates/${id}/versions`)) }]
            : []
        }
      />

      <Row className="g-3">
        <Col lg={7}>
          <Card className="mb-3">
            <Card.Body>
              <div className={styles.editorCol}>
                <div className="flex-grow-1">
                  <Form.Group className="mb-2">
                    <Form.Label>Template name</Form.Label>
                    <Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </Form.Group>
                  <Row className="g-2 mb-2">
                    <Col md={6}>
                      <Form.Label>Channel</Form.Label>
                      <Form.Select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
                        <option value="teams">Teams</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="email">Email</option>
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Label>Use case</Form.Label>
                      <Form.Select value={form.useCase} onChange={(e) => setForm({ ...form, useCase: e.target.value })}>
                        <option value="Booking Confirmation">Booking Confirmation</option>
                        <option value="Reminder">Reminder</option>
                        <option value="MOM Ready">MOM Ready</option>
                        <option value="Cancellation">Cancellation</option>
                      </Form.Select>
                    </Col>
                  </Row>
                  <Form.Group className="mb-2">
                    <Form.Label>Subject (email)</Form.Label>
                    <Form.Control value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="{{meeting_title}}" />
                  </Form.Group>
                  <Form.Label>Body</Form.Label>
                  <SimpleMDE value={form.bodyMarkdown} onChange={(v) => setForm({ ...form, bodyMarkdown: v })} options={mdeOptions} />
                </div>
                <div className={styles.varSidebar}>
                  <div className="small text-muted mb-2">Variables</div>
                  <div className="d-flex flex-column gap-1">
                    {templateVariableList.map((v) => (
                      <Button key={v} size="sm" variant="light" className="text-start text-truncate" onClick={() => insertVar(v)}>
                        {v}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={5}>
          <Card className="mb-3">
            <Card.Header>Preview</Card.Header>
            <Card.Body>
              <Nav variant="tabs" className="mb-2">
                {['teams', 'whatsapp', 'email'].map((t) => (
                  <Nav.Item key={t}>
                    <Nav.Link active={previewTab === t} onClick={() => setPreviewTab(t)} className="py-1 px-2 small text-capitalize">
                      {t}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
              {previewTab === 'teams' && (
                <div className={styles.previewTeams}>
                  <div className="fw-semibold mb-2">MeetingHub</div>
                  <div className="small" dangerouslySetInnerHTML={{ __html: previewRendered.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }} />
                  <Button size="sm" className="mt-2">
                    Join
                  </Button>
                </div>
              )}
              {previewTab === 'whatsapp' && (
                <div className={styles.previewPhone}>
                  <div className="small bg-white rounded p-2">{previewRendered}</div>
                </div>
              )}
              {previewTab === 'email' && (
                <div className="border rounded p-2 small">
                  <div className="text-muted">To: attendee@example.com</div>
                  <div className="fw-semibold">{previewSubject || form.name}</div>
                  <hr />
                  <div>{previewRendered}</div>
                </div>
              )}
              <div className="mt-3">
                <div className="fw-semibold small mb-1">Validation</div>
                <div className={styles.validationLine}>{detail?.validation?.requiredOk ? '✓' : '✗'} All required fields present</div>
                <div className={styles.validationLine}>{detail?.validation?.variablesMapped ? '✓' : '✗'} All variables mapped</div>
                <div className={styles.validationLine}>{detail?.validation?.arabicOk ? '✓' : '✗'} Arabic translation</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className={styles.stickyBar}>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button variant="secondary" onClick={saveDraft}>
            Save draft
          </Button>
          <Button disabled={!canPublish} onClick={publish}>
            Publish
          </Button>
          {!canPublish && (
            <Badge bg="warning" text="dark">
              Fix validation to publish
            </Badge>
          )}
          <Button as={Link} variant="outline-secondary" to={platformUrl('templates')}>
            Back
          </Button>
        </div>
      </div>
    </div>
  );
}
